import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import { authEase } from './authMotion';

interface AuthFieldProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: string;
  optional?: boolean;
  valid?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const AuthField: React.FC<AuthFieldProps> = ({
  label,
  htmlFor,
  error,
  hint,
  optional,
  valid,
  children,
  className,
}) => (
  <motion.div
    className={cn('space-y-2', className)}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, ease: authEase }}
  >
    <label htmlFor={htmlFor} className="flex items-center gap-2 text-sm font-medium text-slate-700">
      {label}
      {optional ? <span className="font-normal text-slate-400">(optional)</span> : null}
      {valid && !error ? (
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="ml-auto flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100"
          aria-hidden
        >
          <Check className="h-2.5 w-2.5 text-emerald-600" />
        </motion.span>
      ) : null}
    </label>
    {children}
    <AnimatePresence mode="wait">
      {error ? (
        <motion.p
          key="error"
          role="alert"
          className="text-sm text-red-500"
          initial={{ opacity: 0, y: -4, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: -4, height: 0 }}
          transition={{ duration: 0.22, ease: authEase }}
        >
          {error}
        </motion.p>
      ) : hint ? (
        <motion.p
          key="hint"
          className="text-xs text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {hint}
        </motion.p>
      ) : null}
    </AnimatePresence>
  </motion.div>
);

interface AuthInputWrapperProps {
  children: React.ReactNode;
  hasError?: boolean;
  isValid?: boolean;
  className?: string;
}

export const AuthInputWrapper: React.FC<AuthInputWrapperProps> = ({
  children,
  hasError,
  isValid,
  className,
}) => {
  const reduceMotion = useReducedMotion();
  const [focused, setFocused] = useState(false);

  return (
    <motion.div
      className={cn(
        'group relative rounded-xl',
        hasError && '[&_input]:border-red-300 [&_input]:focus:ring-red-500/30',
        isValid && !hasError && '[&_input]:border-emerald-300',
        className
      )}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false);
      }}
      animate={
        reduceMotion || !hasError
          ? { x: 0 }
          : { x: [0, -3, 3, -2, 2, 0] }
      }
      transition={{ duration: 0.32 }}
    >
      <motion.span
        className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-blue-500/0"
        animate={{
          opacity: focused && !hasError ? 1 : 0,
          boxShadow:
            focused && !hasError
              ? '0 0 0 2px rgb(59 130 246 / 0.15)'
              : '0 0 0 0px transparent',
        }}
        transition={{ duration: 0.2 }}
        aria-hidden
      />
      {children}
    </motion.div>
  );
};

export const authInputClass = (hasError?: boolean, isValid?: boolean) =>
  cn(
    'block w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900',
    'placeholder:text-gray-400 transition-[border-color,box-shadow,background-color] duration-200',
    'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/25',
    'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-60',
    hasError && 'border-red-300 focus:border-red-500 focus:ring-red-500/25',
    isValid && !hasError && 'border-emerald-300/80 bg-emerald-50/20'
  );

export const authInputWithIconClass = (hasError?: boolean, isValid?: boolean) =>
  cn(authInputClass(hasError, isValid), 'pl-10');
